import React, { useEffect, useReducer, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";

import "../RequestForm.scss";
import { convertDraft, getDraft, updateDraft } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { parseDraftValues, modifyDraftData, resolveServerErrors, hasServerError } from "helpers/requestFormHelper";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import { AddressProvider } from "pages/shipper/RequestForm1/components/Schedule/AddressProvider";
import { initialState, reducer, RequestFormActionType } from "../RequestFormState";
import Header from "./Header/Header";
import OrganisationForm from "./Organisation/OrganisationForm";
import ScheduleForm from "./Schedule/ScheduleForm";
import PackingListForm from "./PackingList/PackingListForm";
import ServiceForm from "./Service/ServiceForm";
import ProviderForm from "./Provider/ProviderForm";
import QuotationGenerationForm from "./CreateShipment/QuotationGenerationForm";

type RequestFormProps = {
  isQuoteRequest: boolean;
  isEditShipment?: boolean;
  isNewShipment?: boolean;
};

function RequestForm(props: RequestFormProps) {
  const { isQuoteRequest, isEditShipment } = props;
  const shouldValidateSections = useRef(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [quoteRequestGenerated, setQuoteRequestGenerated] = useState(false);
  const [isInitialize, setIsInitialize] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const draftId = searchParams.get("draft_id");
  const isDuplicated = searchParams.get("is_duplicated");
  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      // packing_list: initialPackingList()
    },
  });
  const { getValues, handleSubmit } = methods;
  const { permissions } = usePermissions();
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const formStepRefs = {
    [FormStepTypes.ORGANISATION]: useRef<any>(null),
    [FormStepTypes.PROVIDER]: useRef<any>(null),
  };
  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};
  const [shipmentId, setShipmentId] = useState("");

  useEffect(() => {
    if (isQuoteRequest && !JSON.parse(permissions || "{}")?.permissions?.quoting?.create_quote_requests)
      navigate("/access-denied");
    if (!isQuoteRequest && !JSON.parse(permissions || "{}")?.permissions?.quoting?.create_quick_requests)
      navigate("/access-denied");
  }, [isQuoteRequest]);

  useEffect(() => {
    if (!currentActiveStep || currentActiveStep === FormStepTypes.CONFIRMATION) {
      searchParams.set("active_step", FormStepTypes.ORGANISATION);
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    if (!draftId || getValues("organizational.location")) {
      return;
    }
    shouldValidateSections.current = true;
    getDraft(draftId, !isQuoteRequest)
      .then((res) => {
        if (Object.keys(res.data).length && res.data?.organizational) {
          methods.reset(modifyDraftData(res.data, isDuplicated, isQuoteRequest, enable_new_address_structure));
        }
      })
      .catch((err) => {
        errorToast(err);
      });
  }, [draftId, isQuoteRequest, isDuplicated]);

  const handleDraftSave = async () => {
    if (currentActiveStep && currentActiveStep !== FormStepTypes.CONFIRMATION) {
      if (formStepRefs[currentActiveStep]?.current?.checkStepValidation) {
        formStepRefs[currentActiveStep].current.checkStepValidation();
      }
      let isValid = true;
      if (FormStepValidationTypes[currentActiveStep] && !hasServerError(methods.formState.errors)) {
        isValid = await methods.trigger(FormStepValidationTypes[currentActiveStep]);
      }
      if (isValid && draftId && getValues("organizational.location")) {
        return updateDraft(parseDraftValues(getValues()), draftId, !isQuoteRequest)
          .then(() => {
            dispatch({
              type: RequestFormActionType.UPDATE_DRAFT_SAVE,
              payload: { key: currentActiveStep, value: true },
            });
            handleSuccessfullyDraftSaved();
          })
          .catch((err) => {
            resolveServerErrors(err, { ...getValues() }, methods.setError);
          });
      }
    }
  };

  const handleSuccessfullyDraftSaved = () => {
    if (currentActiveStep === FormStepTypes.ORGANISATION) {
      if (isQuoteRequest) {
        formStepRefs[FormStepTypes.PROVIDER].current?.refetchProviders();
      }
      if (isDuplicated) {
        searchParams.delete("is_duplicated");
        setSearchParams(searchParams);
      }
    }
  };

  const handleAccordionChange = (eventKey: string | null) => {
    handleDraftSave();
    if (!eventKey) {
      searchParams.delete("active_step");
    } else {
      searchParams.set("active_step", eventKey);
    }
    setSearchParams(searchParams);
  };

  const validateStepAndFocusOnError = async () => {
    let isValidate = true;
    const stepPriorityOrder = [
      FormStepTypes.ORGANISATION,
      FormStepTypes.SCHEDULE,
      FormStepTypes.PACKING_LIST,
      FormStepTypes.SERVICE,
      FormStepTypes.PROVIDER,
    ];
    // eslint-disable-next-line no-restricted-syntax
    for (const stepName of stepPriorityOrder) {
      // eslint-disable-next-line no-await-in-loop
      const isValid = await validateSection(stepName);
      if (!isValid) {
        handleAccordionChange(stepName);
        isValidate = false;
        break;
      }
    }
    return isValidate;
  };

  const onSubmit = async () => {
    const isValid = await validateStepAndFocusOnError();
    if (!isValid) {
      return;
    }
    setIsInitialize(true);
    await handleDraftSave();
    convertDraft(draftId!, !isQuoteRequest)
      .then((res) => {
        setQuoteRequestGenerated(true);
        setShipmentId(res.data.shipment_id);
        searchParams.set("active_step", FormStepTypes.CONFIRMATION);
        setSearchParams(searchParams);
      })
      .catch((err) => {
        resolveServerErrors(err, { ...getValues() }, methods.setError);
        errorToast(err);
        setIsInitialize(false);
      });
  };

  const handleSubmitClick = () => {
    if (submitBtnRef.current) {
      submitBtnRef.current.click();
    }
  };

  const onError = () => {
    validateStepAndFocusOnError();
  };

  const validateSection = async (sectionName: string) => {
    let isCustomValidated = true;
    if (formStepRefs[sectionName]?.current?.checkStepValidation) {
      isCustomValidated = formStepRefs[sectionName].current.checkStepValidation();
    }
    let isValid = true;
    if (FormStepValidationTypes[sectionName] && !hasServerError(methods.formState.errors)) {
      isValid = await methods.trigger(FormStepValidationTypes[sectionName]);
    }
    return isValid && isCustomValidated;
  };

  const updateSectionValidation = async (sectionName: string) => {
    const isValid = await validateSection(sectionName);
    if (isValid) {
      dispatch({
        type: RequestFormActionType.UPDATE_DRAFT_SAVE,
        payload: { key: sectionName, value: true },
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)} className="request-form" id="request-form">
        <Header state={state} isQuoteRequest={isQuoteRequest} isEditShipment={isEditShipment} isNewShipment />
        <Accordion onSelect={handleAccordionChange} activeKey={currentActiveStep || ""}>
          <OrganisationForm
            state={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
            childRef={formStepRefs[FormStepTypes.ORGANISATION]}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
            isNewShipment
          />
          <AddressProvider>
          <ScheduleForm
            formState={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
            isShipment={false}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
            isNewShipment
          />
          </AddressProvider>
          <PackingListForm
            state={state}
            dispatch={dispatch}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
          />
          <ServiceForm
            state={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
            validateSection={updateSectionValidation}
            shouldValidateSection={shouldValidateSections}
          />
          {isQuoteRequest && (
            <ProviderForm
              state={state}
              dispatch={dispatch}
              childRef={formStepRefs[FormStepTypes.PROVIDER]}
              validateSection={updateSectionValidation}
              shouldValidateSection={shouldValidateSections}
              isNewShipment
            />
          )}
          <QuotationGenerationForm
            shipmentId={shipmentId}
            onSubmitClick={handleSubmitClick}
            quoteRequestGenerated={quoteRequestGenerated}
            setIsInitialize={setIsInitialize}
            isInitialize={isInitialize}
          />
        </Accordion>
        <button ref={submitBtnRef} type="submit" className="form-submit">
          submit
        </button>
      </form>
    </FormProvider>
  );
}

export default RequestForm;

RequestForm.defaultProps = {
  isEditShipment: false,
  isNewShipment: false,
};
